import { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import DOMPurify from 'isomorphic-dompurify';
import { useInternalAnalytics } from '@fusion/reporting';
import { ENV } from '@/constants';
import { fetchCampaign } from '@/hooks';
import { SubmitIcon, ChatIcon, AnouncementIcon } from '@/components/icons';
import CampaignPreviewFooter from '@/components/DiscoverCalls/CampaignPreviewFooter';
import InpartBenefitsMobile from '@/components/DiscoverCalls/InpartBenefitsMobile';
import CampaignPreviewHeader from '@/components/DiscoverCalls/CampaignPreviewHeader';
import InpartBenefitsDesktop from '@/components/DiscoverCalls/InpartBenefitsDesktop';
const captchaSiteKey = ENV.NEXT_PUBLIC_CAPTCHA_SITE_KEY;

type CampaignPageProps = InferGetStaticPropsType<typeof getStaticProps>;

const CampaignPage: React.ElementType<CampaignPageProps> = ({ campaign }) => {
  const collect = useInternalAnalytics();
  useEffect(() => {
    if (campaign) {
      collect(
        {
          property: 'ui:discover-public',
          eventType: 'discoverCampaign:view',
          eventDetails: {
            discoverCampaign: {
              id: campaign.id,
            },
          },
        },
        {
          publicEndpoint: {
            captchaSiteKey,
          },
        },
      );
    }
  }, [campaign]);

  const {
    shortId,
    title,
    closingDate,
    openingDate,
    details: { headerImage, contentRendered, opportunityTypesSought },
  } = campaign;

  const benefits = [
    {
      text: 'Receive the latest campaigns from industry seeking solutions and expertise',
      icon: <AnouncementIcon className="w-6 h-6 shrink-0 flex-none" />,
    },
    {
      text: 'Get introductions to companies, collaborate on projects and commercialise technology',
      icon: <ChatIcon className="w-6 h-6 shrink-0 flex-none" />,
    },
    {
      text: 'Submit research projects, assets, technologies, academic researchers, company profiles, or centres of excellence',
      icon: <SubmitIcon className="w-6 h-6 shrink-0 flex-none" />,
    },
  ];

  const browserTitle = `${campaign.title} | ${ENV.NEXT_PUBLIC_ORGANISATION_NAME} Campaigns`;

  // Dynamically reserve space for the footer
  const footerWrapperRef = useRef<HTMLDivElement | null>(null);
  const [footerSpace, setFooterSpace] = useState(0);

  useEffect(() => {
    const wrapper = footerWrapperRef.current;
    if (!wrapper) return;

    const target = (wrapper.firstElementChild as HTMLElement) || wrapper;

    const measure = () => {
      const h = Math.ceil(target.getBoundingClientRect().height || 0);
      // Only reserve space when the footer is fixed (phones <640px)
      const isPhone = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
      setFooterSpace(isPhone ? h : 0);
    };

    measure();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(target);
    }

    const onResize = () => measure();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div
      className="pt-4 flex flex-col items-center gap-6 w-full lg:flex-row lg:items-start lg:justify-center lg:max-w-screen-lg lg:mx-auto xl:max-w-none md:pt-[70px]"
      style={{ paddingBottom: footerSpace }}
    >
      <div className="hidden bg-content-warning bg-content-success bg-content-error bg-content-neutral" />
      <Head>
        <title>{browserTitle}</title>
      </Head>
      <div className="w-full flex justify-center lg:hidden flex-col">
        <div ref={footerWrapperRef}>
          <CampaignPreviewFooter shortId={shortId} />
        </div>
        <InpartBenefitsMobile benefits={benefits} />
      </div>

      <div className="bg-general-neutral-white border rounded-lg p-6 w-full lg:flex-none lg:max-w-2xl xl:max-w-3xl border-b-outline-neutral-light">
        <CampaignPreviewHeader
          title={title}
          shortId={shortId}
          openingDate={openingDate}
          closingDate={closingDate}
          opportunityTypesSought={opportunityTypesSought}
          headerImage={headerImage}
        />
        <div
          className="relative mb-2.5 [&_h1]:text-[20px] [&_h1]:font-bold [&_h1]:mt-10 [&_h1]:mb-6 [&_h2]:text-[20px] [&_h2]:my-2 [&_h3]:text-[20px] [&_h3]:my-2 [&_h4]:text-[20px] [&_h4]:my-2 [&_p]:mb-5 [&_p]:text-body-lg [&_li]:leading-[1.5] [&_ul]:mb-5 [&_a]:text-content-brand [&_a]:hover:text-content-brand-hover [&_a]:underline [&_a]:break-words"
          dangerouslySetInnerHTML={{ __html: contentRendered }}
        />
      </div>

      <div className="hidden lg:flex flex-col gap-4 max-w-[330px] mb-5 [&_h2]:text-[18px]">
        <CampaignPreviewFooter shortId={shortId} />
        <InpartBenefitsDesktop benefits={benefits} />
      </div>
    </div>
  );
};

export const getStaticProps: GetStaticProps<{
  campaign: Awaited<ReturnType<typeof fetchCampaign>>['data']['data'];
}> = async ({ params }) => {
  try {
    const { campaignId } = params as { campaignId: string };
    const campaign = (await fetchCampaign({ campaignId })).data.data;
    const { contentRendered } = campaign.details;

    return {
      props: {
        campaign: {
          ...campaign,
          details: { ...campaign.details, contentRendered: contentRendered && DOMPurify.sanitize(contentRendered) },
        },
      },
      revalidate: 60, // seconds
    };
  } catch (err) {
    return {
      notFound: true,
      revalidate: 60, // seconds
    };
  }
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default CampaignPage;
